import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";

import { useEmailOutbox } from "./hooks/queries/useEmails";
import { useSeminarInputs } from "./hooks/seminars/useSeminarInputs";

import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import type { MenuItemStyles } from "react-pro-sidebar";

import Logo from "./img/biteg-logo.png";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import CreateIcon from "@mui/icons-material/Create";
import ListIcon from "@mui/icons-material/List";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import Badge from "@mui/material/Badge";
import { AppIconButton } from "./components/shared/AppIconButton";
import type { ReactNode } from "react";

import { useAuth } from "./contexts/AuthContext";
import { ROLE } from "./constants/roles";

// Custom palette (from tailwind.config.js)
// Values taken from production's own sidebar CSS (react-pro-sidebar 0.7's
// vendored stylesheet, inlined in main's src/index.css): `.pro-sidebar` sets
// `color: black`, so labels AND idle icons are black — the greys this file had
// are what made the menu read lighter than the app users know.
const COLORS = {
  bg: "#F7F7F7", // navbargray
  active: "#F4A305", // orange-900
  text: "#000000",
  icon: "#000000",
};

/** A single sidebar navigation entry. */
interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
}

export interface PageLayoutProps {
  logOut: () => void;
}

const PageLayout = ({ logOut }: PageLayoutProps) => {
  const [collapsed, setCollapsed] = useState(false); // desktop icon-only mode
  const [toggled, setToggled] = useState(false); // mobile overlay open

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { userDetails } = useAuth();

  // `role` is the populated object `{ id, name }` (or, per the type, a bare id);
  // only the object form carries the admin id we gate on. Mirrors the old
  // `userDetails?.role?.id` read.
  const role = userDetails?.role;
  const roleId = typeof role === "object" ? role?.id : undefined;

  // The Rechnungen nav is disabled until a seminar is selected; read that live
  // from the seminar store (formerly relayed through the outlet).
  const seminarInputs = useSeminarInputs();
  // Outbox badge: pending (future) count, straight from the live email cache.
  const { data: pendingEmails = [] } = useEmailOutbox({
    mode: "future",
    email: userDetails?.email,
  });

  // The seminar workspace (selection, filters, edit mode) + its participant list
  // now live entirely in useSeminarStore / useInvoiceDerived and are read
  // directly by the seminar + invoice views — PageLayout no longer projects them.

  const navItems: NavItem[] = [
    { to: "/seminare/semverwaltung", label: "Seminarverwaltung", icon: <CreateIcon /> },
    { to: "/seminare/seminarzusammenfassung", label: "Kompl. Seminarliste", icon: <ListIcon /> },
    {
      to: "/seminare/bestsemverwaltung",
      label: "Bestätigte Seminare",
      icon: <CheckCircleOutlinedIcon />,
    },
    { to: "/seminare/raume/verwaltung", label: "Räume", icon: <ApartmentIcon /> },
    { to: "/suche", label: "Suche", icon: <SearchIcon /> },
    { to: "/seminare/referenten/verwaltung", label: "Referenten", icon: <PersonIcon /> },
    { to: "/kunden/verwaltung", label: "Kunden", icon: <GroupsIcon /> },
    {
      to: "/rechnungen",
      label: "Rechnungen",
      icon: <DescriptionIcon />,
      disabled: seminarInputs.id === undefined,
    },
    { to: "/emailexport", label: "Werbung E-Mail Export", icon: <EmailIcon /> },
    {
      to: "/emailpreview",
      label: "E-Mail Ausgangsordner",
      icon: (
        <Badge
          badgeContent={pendingEmails.length}
          color="primary"
          slotProps={{ badge: { "data-testid": "outbox-badge" } as never }}
        >
          <SendIcon />
        </Badge>
      ),
    },
  ];

  // Matches production's `.pro-sidebar .pro-menu .pro-menu-item` (12px / 16px /
  // 600) and `.pro-inner-item` (8px 20px padding, so the row height comes from
  // the type rather than a fixed 44px), with the active `bg-white
  // text-orange-900 rounded-xl m-1` pill.
  const menuItemStyles: MenuItemStyles = {
    button: ({ active, disabled }) => ({
      borderRadius: "0.75rem",
      margin: "4px",
      height: "auto",
      padding: "8px 20px",
      fontSize: "12px",
      lineHeight: "16px",
      fontWeight: 600,
      letterSpacing: "0.4px",
      color: active ? COLORS.active : COLORS.text,
      backgroundColor: active ? "#ffffff" : "transparent",
      opacity: disabled ? 0.3 : 1,
      "&:hover": { backgroundColor: "#ffffff", color: COLORS.active },
    }),
    // production's `.pro-icon-wrapper`: a 26px square slot with a 10px gap
    // (v1 defaults to a wider slot, which pushed the labels right).
    icon: ({ active }) => ({
      color: active ? COLORS.active : COLORS.icon,
      width: "26px",
      minWidth: "26px",
      height: "26px",
      marginRight: "10px",
    }),
  };

  const closeMobile = () => setToggled(false);

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      {/* Mobile hamburger (only below the lg breakpoint) */}
      <AppIconButton
        label="Menü öffnen"
        onClick={() => setToggled(true)}
        className="lg:hidden!"
        sx={{ position: "fixed", top: 8, left: 8, zIndex: 1200, bgcolor: "#fff", boxShadow: 1 }}
      >
        <MenuIcon />
      </AppIconButton>

      <Sidebar
        collapsed={collapsed}
        toggled={toggled}
        onBackdropClick={closeMobile}
        breakPoint="lg"
        backgroundColor={COLORS.bg}
        width="270px"
        collapsedWidth="80px"
        rootStyles={{ borderRight: "1px solid #e5e7eb", height: "100vh" }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header: logo + collapse / close */}
          <div className="flex items-center gap-4 px-5 h-16 shrink-0">
            <AppIconButton
              label={collapsed ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
              onClick={() => setCollapsed((c) => !c)}
              className="hidden! lg:inline-flex!"
              sx={{ color: COLORS.text, padding: 0 }}
            >
              <ArrowForwardIosIcon
                className={collapsed ? "" : "rotate-180"}
                sx={{ "&:hover": { color: COLORS.active } }}
              />
            </AppIconButton>
            <AppIconButton
              label="Menü schließen"
              onClick={closeMobile}
              className="lg:hidden!"
              sx={{ color: COLORS.text, padding: 0 }}
            >
              <CloseIcon />
            </AppIconButton>
            {!collapsed && <img className="h-8" src={Logo} alt="BITEG" />}
          </div>

          {/* Main nav */}
          <Menu menuItemStyles={menuItemStyles} style={{ flex: 1, overflowY: "auto" }}>
            {navItems.map((item) => (
              <MenuItem
                key={item.to}
                icon={item.icon}
                active={pathname === item.to}
                disabled={item.disabled}
                component={<Link to={item.to} />}
                onClick={closeMobile}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Footer */}
          <Menu menuItemStyles={menuItemStyles} className="shrink-0 border-t border-gray-200 py-1">
            {roleId === ROLE.ADMIN && (
              <MenuItem
                icon={<SettingsIcon />}
                onClick={() => {
                  navigate("/options");
                  closeMobile();
                }}
              >
                Benutzer
              </MenuItem>
            )}
            <MenuItem icon={<LogoutIcon />} onClick={() => logOut()}>
              Abmelden
            </MenuItem>
          </Menu>
        </div>
      </Sidebar>

      <main
        className="flex-1 overflow-y-auto"
        // scrollbar-gutter reserves the scrollbar's width even when a page is
        // short enough not to scroll — otherwise the whole content column
        // shifts sideways when navigating between scrolling and non-scrolling
        // modules.
        style={{ scrollbarGutter: "stable" }}
      >
        {/* Pages self-source their data from feature stores/queries now — the
            outlet no longer relays an app-wide context bag. */}
        <Outlet />
      </main>
    </div>
  );
};

export default PageLayout;
